<template>
  <PageHeader :showBreadcrumb="true"/>
  <div class="container p-3 text-center">
    <MnRow>
      <MnColumn cols="12">
        <!-- Initial Exam Documents Section -->
        <h3>Initial Exam Documents (QAE and TCA)</h3>
        <p>Below, please upload all files requested for the both the Quality Assurance Exam (QAE) and Triennial
          Compliance Assessment (TCA), if applicable. Each upload section is for a specific part of the exam, and
          multiple files can be uploaded. Please make sure you select the correct type of file before uploading using
          the drop downs.</p>
        <p>Some documents are requested at the start of the exam and to determine file samples. These are in the
          "Initial Exam Documents" section. During the exam, MDH may reach out to ask for clarifications. These can be
          uploaded under "Clarification Requests" section.</p>
        <p>You can review all documents provided by MDH in your "QA Exam documents" section.</p>
      </MnColumn>
    </MnRow>

    <hr/>

    <!-- Display All Combined Exams -->
    <MnRow>
      <MnColumn cols="12">
        <FormSelectGroup
            label="Select Exam"
            inputId="combined-exam-input"
            :required="true"
            state
        >
          <template #default>
            <PvSelect
                id="combined-exam-input"
                v-model="selectedCombined"
                :options="combinedExamOptions"
                :optionLabel="formatExamLabel"
                optionValue="examProductId"
                placeholder="-- Select Entity • Exam • Product --"
                filter
                autoOptionFocus
                @change="onChangeCombined"
            />
          </template>
        </FormSelectGroup>
      </MnColumn>
    </MnRow>

    <hr/>

    <div v-if="selectedCombined">
      <!-- Clarification Requests Section -->
      <h3>Initial Document Request</h3>

      <h5>Initial Document Request Files</h5>
      <ul class="text-start">
        <li>Please upload all files requested in the Initial Document Request (IDR) sent to you by MDH. You may upload Zip files, PDFs, Word, Excel, or WAV files.</li>
        <li>Please reference the IDR for the corresponding section (Background, Quality Assessment and Improvement, etc.) to upload documents.</li>
        <li>Please also reference the IDR Instructions for how to group files, folders, subfolders, etc., and naming conventions.</li>
      </ul>

      <h5>File Universes and Sample Files</h5>
      <ul class="text-start">
        <li>Please upload the File Universes when requested by MDH. You may upload PDFs, Word, or Excel files.</li>
        <li>Please upload Sample Files when requested by MDH.</li>
        <li>Select either “File universe” or “Sample Files” before uploading.</li>
      </ul>

      <!-- Documents Upload/Delete Section -->
      <MnRow>
        <!-- IDR Document Request -->
        <MnColumn cols="6">
          <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
            <FormSelectGroup
                label="Select IDR Document Module"
                inputId="idr-module-type"
                :required="true"
                state
            >
              <template #default>
                <PvSelect
                    id="idr-module-type"
                    v-model="selectedIDRModuleType"
                    :options="idrModuleOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="-- Select IDR Module Type --"
                    @change="onChangeIdrModule"
                />
              </template>
            </FormSelectGroup>
            <MNFileUpload
                v-if="selectedIDRModuleType"
                module-type="Quality Assurance File Uploads"
                label="IDR Document Files"
                ref="idrDocumentFiles"
                name="idrDocumentFiles"
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/zip,audio/wav"
                url="/undefined"
                :examProduct="selectedCombined"
                :qaModuleType="selectedIDRModuleType"
                :fileLimit="20"
            />
          </div>
        </MnColumn>

        <!-- Sample Files -->
        <MnColumn cols="6">
          <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
            <FormSelectGroup
                label="Select File Universe and Sample Files"
                inputId="sample-file-type"
                :required="true"
                state
            >
              <template #default>
                <PvSelect
                    id="sample-file-type"
                    v-model="selectedSampleFileType"
                    :options="sampleFileOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="-- Select File Type --"
                    @change="onChangeSampleFileType"
                />
              </template>
            </FormSelectGroup>
            <MNFileUpload
                v-if="selectedSampleFileType"
                module-type="Quality Assurance File Uploads"
                label="Sample Files"
                ref="sampleFiles"
                name="sampleFiles"
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                url="/undefined"
                :examProduct="selectedCombined"
                :qaModuleType="selectedSampleFileType"
                :fileLimit="20"
            />
          </div>
        </MnColumn>
      </MnRow>

      <!-- Clarification Requests Section -->
      <hr/>
      <h3>Clarification Requests</h3>

      <!-- Converted QA/TCA clarification plain text to headings and lists -->
      <h5>QA Exam clarifications</h5>
      <ul class="text-start">
        <li>This is a place to upload additional documents or clarifications requested by MDH related to items in the QAE. You may upload Zip files, PDFs, Word, or Excel files.</li>
        <li>Select the examination module (e.g. Quality assessment and Improvement, Access, Availability &amp; Continuity of Care) before uploading.</li>
        <li>You may upload multiple files for each module.</li>
        <li>You may upload files for multiple modules by changing the dropdown.</li>
      </ul>

      <h5>TCA clarifications</h5>
      <ul class="text-start">
        <li>This is a place to upload additional documents or clarifications requested by MDH related to items in the TCA. You may upload Zip files, PDFs, Word, or Excel files.</li>
        <li>Select the examination module (e.g. EL2 Information Systems, EL8 PIP Interim Reports) before uploading.</li>
        <li>You may upload multiple files for each module.</li>
        <li>You may upload files for multiple modules by changing the dropdown.</li>
      </ul>

      <MnRow>
        <!-- QA Exam Clarifications -->
        <MnColumn cols="6">
          <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
            <FormSelectGroup
                label="QA Clarification Module Type"
                inputId="qa-module-type"
                :required="true"
                state
            >
              <template #default>
                <PvSelect
                    id="qa-module-type"
                    v-model="selectedQAModuleType"
                    :options="qaModuleOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="-- Select QA Module Type --"
                    @change="onChangeQaClarification"
                />
              </template>
            </FormSelectGroup>
            <MNFileUpload
                v-if="selectedQAModuleType"
                module-type="Quality Assurance File Uploads"
                label="QA Exam Clarifications"
                ref="qaClarificationFiles"
                name="qaClarificationFiles"
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/zip"
                url="/undefined"
                :examProduct="selectedCombined"
                :qaModuleType="selectedQAModuleType"
                :fileLimit="20"
            />
          </div>
        </MnColumn>

        <!-- TCA Clarifications -->
        <MnColumn cols="6">
          <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
            <FormSelectGroup
                label="TCA Clarification Module Type"
                inputId="tca-module-type"
                :required="true"
                state
            >
              <template #default>
                <PvSelect
                    id="tca-module-type"
                    v-model="selectedTCAModuleType"
                    :options="tcaModuleOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="-- Select TCA Module Type --"
                    @change="onChangeTcaClarification"
                />
              </template>
            </FormSelectGroup>
            <MNFileUpload
                v-if="selectedTCAModuleType"
                module-type="Quality Assurance File Uploads"
                label="TCA Clarification Files"
                ref="tcaClarificationFiles"
                name="tcaClarificationFiles"
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/zip"
                url="/undefined"
                :examProduct="selectedCombined"
                :qaModuleType="selectedTCAModuleType"
                :fileLimit="20"
            />
          </div>
        </MnColumn>

        <!-- Other Documents: separate uploader with manual description -->
        <MnColumn cols="6">
          <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
            <FormSelectGroup
                label="Other Documents"
                inputId="other-description"
                :required="true"
                state
            >
              <template #default>
                <input
                    type="text"
                    id="other-description"
                    class="form-control"
                    v-model="customOtherDescription"
                    maxlength="100"
                    placeholder="Specify Document Type (Max 100 characters)"
                />
              </template>
              <template #hint>
                <small class="text-muted">{{ customOtherDescription.length }}/100 characters</small>
              </template>
            </FormSelectGroup>

            <MNFileUpload
                v-if="customOtherDescription"
                module-type="Quality Assurance File Uploads"
                label="Other Files"
                ref="otherFiles"
                name="otherFiles"
                accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/zip"
                url="/undefined"
                :examProduct="selectedCombined"
                :qaModuleType="'Other'"
                :qaModuleTypeDescription="customOtherDescription"
                :fileLimit="20"
            />
          </div>
        </MnColumn>
      </MnRow>
    </div>
  </div>
</template>

<script setup>
import PageHeader from "@/components/common/PageHeader.vue";
import MNFileUpload from "@/components/common/MNFileUpload.vue";
import FormSelectGroup from "@/components/common/FormSelectGroup.vue";
import MnRow from "@/components/common/MnRow.vue";
import MnColumn from "@/components/common/MnColumn.vue";
import {useAppStore} from "@/stores/appStore.js";
import {computed, ref} from "vue";
import {useAuthStore} from "@/stores/authStore.js";
import {useForm} from "@/composables/form.js";
import {QualityAssuranceFileUploadsModel} from '@/models/QualityAssuranceFileUploadsModel';
import {
  IDR_MODULE_TYPES,
  SAMPLE_FILE_TYPES,
  QA_EXAM_CLARIFICATION_MODULE_TYPES,
  TCA_CLARIFICATION_MODULE_TYPES,
  getAsFormOptions
} from '@/utils/constants.js';

const store = useAuthStore();
const appStore = useAppStore();
const selectedCombined = ref();

const selectedIDRModuleType = ref(null);
const selectedSampleFileType = ref(null);
const selectedQAModuleType = ref(null);
const selectedTCAModuleType = ref(null);
const customOtherDescription = ref('');

const idrModuleOptions = getAsFormOptions(IDR_MODULE_TYPES);
const sampleFileOptions = getAsFormOptions(SAMPLE_FILE_TYPES);
const qaModuleOptions = getAsFormOptions(QA_EXAM_CLARIFICATION_MODULE_TYPES);
const tcaModuleOptions = getAsFormOptions(TCA_CLARIFICATION_MODULE_TYPES);

const idrDocumentFiles = ref();
const sampleFiles = ref();
const qaClarificationFiles = ref();
const tcaClarificationFiles = ref();
const otherFiles = ref();

const formState = useForm(new QualityAssuranceFileUploadsModel());

const combinedExamOptions = computed(() => {
  return appStore.getAllEntityExamProductOptions();
});

const onChangeCombined = async ({value}) => {
  if (!value) return;
  selectedCombined.value = value;
  formState.state.data.examProductId = value;
  await clearAllUploaders();
};

const onChangeIdrModule = async ({value}) => {
  if (!value) return;
  await clearIdrUploader();
};

const onChangeSampleFileType = async ({value}) => {
  if (!value) return;
  await clearSampleFilesUploader();
};

const onChangeQaClarification = async ({value}) => {
  if (!value) return;
  await clearQaClarificationUploader();
};

const onChangeTcaClarification = async ({value}) => {
  if (!value) return;
  await clearTcaClarificationUploader();
};

const formatExamLabel = (option) => {
  if (!option) return '';
  return `(${option.entityName} • ${option.examType} • ${option.productName})`;
};


// Generic uploader clearing function
const clearUploader = (uploader) => {
  uploader.value?.clear();
};

const clearAllUploaders = async () => {
  [idrDocumentFiles, sampleFiles, qaClarificationFiles, tcaClarificationFiles, otherFiles]
      .forEach(clearUploader);
};

const clearIdrUploader = async () => clearUploader(idrDocumentFiles);
const clearSampleFilesUploader = async () => clearUploader(sampleFiles);
const clearQaClarificationUploader = async () => clearUploader(qaClarificationFiles);
const clearTcaClarificationUploader = async () => clearUploader(tcaClarificationFiles);
// clearOtherUploader removed because the separate 'Other' uploader is cleared via clearAllUploaders
</script>

<style scoped>
</style>

